/**
 * Permit packet manifest (capability tiers — companion §1.2 / §2.8).
 *
 * Shows the Tier-0/1 packet: the typed-document manifest with required flags (derived or
 * from the AHJ sufficiency checklist) and a "Build / refresh packet" action that renders the
 * envelope into the application PDF. Reachable from the permit detail screen by tapping.
 *
 * Wiring (mobile inv. 7): GET /api/permit-applications/:id/packet (useQuery) +
 * POST …/packet (useMutation). Loading / empty / error states render. No mock data.
 * NEVER imports @smartbox/* (inv. 1).
 */

import { View, StyleSheet, ActivityIndicator } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getPacket, buildPacket, uploadPacketDocument, type PacketRow, type PacketDocumentRow } from '../../../src/api/permits';
import { Screen, Text, Card, Button, Badge } from '../../../src/components/ui';
import type { StatusKey } from '../../../src/theme';
import { theme } from '../../../src/theme';
import { extractErrorMessage, humanize } from '../../../src/lib/format';
import { pickDocument } from '../../../src/platform/fileCapture';

function docBadge(status: string): { key: StatusKey; label: string } {
  switch (status) {
    case 'signed_sealed':
    case 'attached':
      return { key: 'paid', label: humanize(status) };
    case 'na':
      return { key: 'draft', label: 'N/A' };
    default:
      return { key: 'overdue', label: 'Missing' };
  }
}

export default function PermitPacketScreen() {
  const { appId } = useLocalSearchParams<{ appId: string }>();
  const queryClient = useQueryClient();

  const packetQuery = useQuery({
    queryKey: ['permit-packet', appId],
    queryFn: () => getPacket(appId!),
    enabled: !!appId,
    staleTime: 30_000,
  });

  const buildMutation = useMutation({
    mutationFn: () => buildPacket(appId!),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['permit-packet', appId] }),
  });

  const attachMutation = useMutation({
    mutationFn: async (docId: string) => {
      const file = await pickDocument();
      if (!file) return null;
      return uploadPacketDocument(docId, { fileName: file.filename, mimeType: file.mimeType, contentBase64: file.contentBase64 });
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['permit-packet', appId] }),
  });

  if (!appId) {
    return (
      <Screen>
        <View style={styles.centeredState} testID="permit-packet-screen">
          <Text variant="body" color="danger">No permit application ID provided.</Text>
        </View>
      </Screen>
    );
  }

  if (packetQuery.isLoading) {
    return (
      <Screen>
        <View style={styles.centeredState} testID="permit-packet-screen">
          <ActivityIndicator size="large" color={theme.color.action.primary} accessibilityLabel="Loading packet" />
          <Text variant="body" color="secondary">Loading…</Text>
        </View>
      </Screen>
    );
  }

  if (packetQuery.isError) {
    return (
      <Screen>
        <View style={styles.centeredState} testID="permit-packet-screen">
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(packetQuery.error, 'Failed to load packet.')}
            </Text>
            <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => packetQuery.refetch()} />
          </Card>
        </View>
      </Screen>
    );
  }

  const packet: PacketRow | null = packetQuery.data ?? null;
  const documents: PacketDocumentRow[] = packet?.documents ?? [];

  return (
    <Screen>
      <View testID="permit-packet-screen" style={styles.section}>
        <View style={styles.header}>
          <Text variant="bodyStrong" style={styles.flex1}>Submission packet</Text>
          <Button
            title={packet ? 'Refresh packet' : 'Build packet'}
            variant="primary"
            size="sm"
            fullWidth={false}
            loading={buildMutation.isPending}
            disabled={buildMutation.isPending}
            onPress={() => buildMutation.mutate()}
            accessibilityLabel="Build or refresh the Tier-0 packet"
          />
        </View>

        {buildMutation.isError ? (
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(buildMutation.error, 'Could not build the packet.')}
            </Text>
          </Card>
        ) : null}

        {!packet ? (
          <Card>
            <Text variant="body" color="secondary">
              No packet yet. Build the packet to render the application PDF and the document manifest.
            </Text>
          </Card>
        ) : documents.length === 0 ? (
          <Card>
            <Text variant="body" color="secondary">The packet has no documents.</Text>
          </Card>
        ) : (
          <View style={styles.list}>
            {documents.map((d) => {
              const b = docBadge(d.status);
              return (
                <Card key={d.id} testID={`packet-doc-${d.doc_type}`}>
                  <View style={styles.header}>
                    <Text variant="bodyStrong" style={styles.flex1}>{humanize(d.doc_type)}</Text>
                    <Badge status={b.key} label={b.label} />
                  </View>
                  <Text variant="small" color="secondary">
                    {d.required ? 'Required' : 'Optional'} · source: {humanize(d.required_source)}
                  </Text>
                  {d.status === 'missing' && d.doc_type !== 'application' && d.doc_type !== 'info_sheet' ? (
                    <Button
                      title="Attach file"
                      variant="secondary"
                      size="sm"
                      fullWidth={false}
                      testID={`packet-attach-${d.doc_type}`}
                      loading={attachMutation.isPending && attachMutation.variables === d.id}
                      disabled={attachMutation.isPending}
                      onPress={() => attachMutation.mutate(d.id)}
                      accessibilityLabel={`Attach a file for ${humanize(d.doc_type)}`}
                    />
                  ) : null}
                </Card>
              );
            })}
          </View>
        )}
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  centeredState: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: theme.spacing.md, paddingHorizontal: theme.spacing['2xl'] },
  section: { gap: theme.spacing.sm },
  list: { gap: theme.spacing.sm },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing.sm },
  flex1: { flex: 1 },
});
